import { RefObject, useEffect } from 'react';

interface UseScrollPaddingFromStickyHeaderOptions {
  /** The sticky element that overlays the top of the scroll container. */
  headerRef: RefObject<HTMLElement | null>;
  /** The scroll container whose scroll-padding-top should clear that header. */
  scrollContainerRef: RefObject<HTMLElement | null>;
  /** Extra clearance to reserve below the header, in px. */
  extraClearance?: number;
  /** Used until the header can be measured. */
  fallbackHeaderHeight?: number;
}

/**
 * Keep a scroll container's `scroll-padding-top` in step with the height of the
 * sticky header that overlays it.
 *
 * `scrollIntoView({ block: 'start' })` lands a target flush with the top of the
 * scrollport, which is underneath a sticky header. `scroll-padding-top` is the
 * fix, but hard-coding it means guessing a height that lives in another
 * component and grows when that component does — a filter bar gaining a row,
 * say. Measuring closes that gap, and keeps closing it.
 *
 * The value is written straight to the element rather than held in state:
 * it is presentation only, and a re-render per resize would be a poor trade.
 */
export function useScrollPaddingFromStickyHeader({
  headerRef,
  scrollContainerRef,
  extraClearance = 0,
  fallbackHeaderHeight = 0,
}: UseScrollPaddingFromStickyHeaderOptions): void {
  useEffect(() => {
    const header = headerRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!header || !scrollContainer) {
      return undefined;
    }

    const applyScrollPadding = (): void => {
      const headerHeight = header.getBoundingClientRect().height || fallbackHeaderHeight;
      scrollContainer.style.scrollPaddingTop = `${Math.round(headerHeight) + extraClearance}px`;
    };

    applyScrollPadding();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(applyScrollPadding);
    observer.observe(header);
    return () => observer.disconnect();
  }, [headerRef, scrollContainerRef, extraClearance, fallbackHeaderHeight]);
}
